import { AngularReactBrowserModule } from '@angular-react/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, Injector, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
// Feature Toggle
import {
  appConfigInjectionToken,
  customPropertyLoaderInjectionToken,
  FeatureToggleModule,
} from '@cemex-core/cmx-rollout-toggle-v1';
import { CmxClientLayoutModule, ICmxClientConfig, AppState } from '@cemex/cmx-client-layout';
import { CmxWebComponentsModule } from '@cmx-web-components/angular';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { environment } from '../environments/environment';
import { CmxCookiebotModule } from '@cemex/cmx-cookiebot-v7';
// Routing
import { AppRoutingModule } from './app-routing.module';
// Components
import { AppComponent } from './app.component';
import { AppFeaturesModule } from './shared/app-features/app-features.module';
import { ActionBarModule } from './shared/action-bar/action-bar.module';
import { appConfigFactory } from './shared/app-features/config/appConfig';
import { toggleConfig } from './shared/app-features/config/toggleConfig';
import { appPropertyLoaderFactory } from './shared/app-features/services/property-loader.service';
// Global Filters
import { GlobalFiltersModule } from './shared/global-filters';
import { GlobalErrorHandler } from '@ordersnproducts/modules/shared/errors/errors-handler';
import { ErrorInterceptor } from '@ordersnproducts/modules/shared/interceptors/error-interceptor';
import { AnalyticsTrackingService } from './services/analytics-tracking.service';
import { EffectsModule, EffectSources } from '@ngrx/effects';
import { CatalogsEffects } from '@ordersnproducts/modules/shared/effects/catalogs.effects';
import { globalFiltersStateKey } from './shared/global-filters/models';
import { FiltersState } from './shared/global-filters/store/reducer';
import { CanEntryCalendarViewGuard } from './calendar-view/guard/calendar-view-entry.guard';
import { CobrowseFeatureService } from './services/cobrowse-feature.service';
import { NewOrderDcoFactoryService } from './vanilla/modules/multiple-order-online/factory/dco-factory/new-order/new-order-dco-factory.service';
import { FormControlMessageError } from '@ordersnproducts/utils/formControlStatusMessage';
import { setAppInjector } from '@ordersnproducts/utils/mix-reference';
import * as AppInjector from '@ordersnproducts/utils/app-injector';
import { ProductDescriptionHomologationService } from '@ordersnproducts/modules/shared/services/product-description-homologation.service';
import { CxUiRootModule } from './cx-ui-root/cx-ui-root.module';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  HttpCemex,
  SessionService,
  ProjectSettings,
  Broadcaster,
  CountlyService,
} from '@cemex-core/angular-services-v8';
import { DeletableEffectSources } from '@ordersnproducts/modules/shared/modules/dynamic-effects/deletable-effect-sources';
import { AuthInterceptorFactory } from './core/interceptors/auth.interceptorFactory';
import {
  IamAuthService,
  IamBaseService,
  IamSettingsService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MSALGuardConfigFactory,
  MsalGuardFromService,
  MSALInstanceFactory,
  MsalInterceptor,
  MSALInterceptorConfigFactory,
  MsalRedirectComponent,
  MsalService,
} from '@cemex-core/msal-services-v1';
// import { AuthInterceptorMsalCustom } from './core/interceptors/auth.interceptorFactory';

export type State = AppState & {
  [globalFiltersStateKey]: FiltersState;
};

const developmentImports = [];

if (environment.ngrxDevTools || window['ngrxDevTools']) {
  /**
   * Store devtools instrument the store retaining past versions of state
   * and recalculating new states. This enables powerful time-travel
   * debugging.
   *
   * To use the debugger, install the Redux Devtools extension for either
   * Chrome or Firefox
   *
   * See: https://github.com/zalmoxisus/redux-devtools-extension
   */
  developmentImports.push(
    StoreDevtoolsModule.instrument({
      name: 'CMX OrdersnProducts Store DevTools',
      maxAge: 25,
      logOnly: true,
    }),
  );
}

const config: ICmxClientConfig = {
  apiHost: environment.apiHost,
  translate: {
    getLanguagesUrl: '/translate/getLanguages',
    translateHost: environment.translations.apiHost,
    translateUrl: '/translate/translate/',
  },
  countlyKey: environment.countlyKey,
  environment: 'dev',
  googleMapsApiKey: environment.mapApiKey,
  rolloutKey: '',
  production: environment.production,
  loginUrl: '/login',
};

@NgModule({
  bootstrap: [AppComponent, MsalRedirectComponent],
  declarations: [AppComponent],
  imports: [
    CxUiRootModule,
    AngularReactBrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    CmxWebComponentsModule.forRoot(),
    CmxClientLayoutModule.forRoot(config),
    EffectsModule.forRoot([CatalogsEffects]),
    /**
     * @ngrx/router-store keeps router state up-to-date in the store.
     */
    StoreRouterConnectingModule.forRoot({
      /**
       They stateKey defines the name of the state used by the router-store reducer.
       This matches the key defined in the map of reducers
       */
      stateKey: 'router',
    }),
    AppRoutingModule,
    AppFeaturesModule,
    FeatureToggleModule.forRoot({ provide: appConfigInjectionToken, useFactory: appConfigFactory }, toggleConfig, {
      provide: customPropertyLoaderInjectionToken,
      useFactory: appPropertyLoaderFactory,
      deps: [Store],
    }),
    GlobalFiltersModule,
    ActionBarModule.forRoot(),
    CmxCookiebotModule.forRoot(),
    ...developmentImports,
  ],
  providers: [
    MsalService,
    MsalGuardFromService,
    MsalBroadcastService,
    IamSettingsService,
    IamBaseService,
    {
      provide: IamAuthService,
      deps: [HttpCemex, MsalService, MsalBroadcastService],
      useClass: IamAuthService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    MsalInterceptor,
    {
      provide: MSAL_INSTANCE,
      deps: [IamSettingsService],
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      deps: [IamSettingsService],
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: (factory: AuthInterceptorFactory) => factory.createInterceptor(),
      deps: [AuthInterceptorFactory],
      multi: true,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: CountlyService,
      useFactory: (sessionService: SessionService) => new CountlyService(sessionService),
      deps: [SessionService],
    },
    HttpCemex,
    SessionService,
    ProjectSettings,
    Broadcaster,
    FormControlMessageError,
    AnalyticsTrackingService,
    CanEntryCalendarViewGuard,
    CobrowseFeatureService,
    NewOrderDcoFactoryService,
    ProductDescriptionHomologationService,
    DeletableEffectSources,
    {
      provide: EffectSources,
      useExisting: DeletableEffectSources,
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor(private injector: Injector) {
    AppInjector.setAppInjector(this.injector);
    setAppInjector(this.injector);
  }
}
